import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  mobileSettingsKeys,
  useMobileSettings,
} from "@/components/pages/mobile-settings/hooks/useMobileSettings";
import {
  createKeyedQueue,
  featuredServiceIdsOf,
  writeFeaturedToScreens,
} from "@/components/pages/guesteria-partners/utils/guesteriaFeatured";
import { mobileSettingsService } from "@/services/mobileSettingsService";
import { MobileScreenSettingsScreenTypes } from "@/types/mobile-settings";

/**
 * The Featured Activities list is stored per guest-app screen. The switch
 * reads the In house list (the one guests open by QR and the default screen
 * of App Customization) and writes the same change to all three lists.
 *
 * In house goes last: the switch shows that list, so a failure on any screen
 * leaves the switch where it was, and the next click finishes the screens
 * that were not written yet (screens already in the wanted state are skipped).
 */
const FEATURED_WRITE_ORDER: readonly MobileScreenSettingsScreenTypes[] = [
  MobileScreenSettingsScreenTypes.BEFORE_CHECKIN,
  MobileScreenSettingsScreenTypes.AFTER_CHECKOUT,
  MobileScreenSettingsScreenTypes.DURING_STAY,
];

/**
 * `PATCH /mobile-settings` replaces the whole list, so two overlapping
 * read-modify-write runs lose one change. Runs of one hotel go one by one.
 */
const enqueueFeaturedWrite = createKeyedQueue();

export interface ServiceFeaturedController {
  /** Service ids in the In house Featured Activities list. */
  featuredIds: ReadonlySet<string>;
  /** False until the In house settings are loaded (loading or failed). */
  isReady: boolean;
  /** True when the In house settings failed to load and there is no data. */
  loadFailed: boolean;
  /**
   * Turns a service on or off in the Featured list of every screen. Each
   * list is re-read right before its write, so a change made meanwhile in
   * App Customization is not overwritten. Rejects if any request fails.
   */
  setFeatured: (serviceId: string, featured: boolean) => Promise<void>;
}

export const useServiceFeatured = (
  hotelId: string | undefined,
): ServiceFeaturedController => {
  const queryClient = useQueryClient();
  const { settings, error } = useMobileSettings(
    hotelId ?? "",
    MobileScreenSettingsScreenTypes.DURING_STAY,
  );

  const featuredIds = useMemo(
    () => new Set(featuredServiceIdsOf(settings)),
    [settings],
  );

  const setFeatured = useCallback(
    async (serviceId: string, featured: boolean): Promise<void> => {
      if (!hotelId) {
        throw new Error("hotelId is required");
      }
      await enqueueFeaturedWrite(hotelId, async () => {
        try {
          await writeFeaturedToScreens(
            FEATURED_WRITE_ORDER,
            serviceId,
            featured,
            {
              getSettings: (screenType) =>
                mobileSettingsService.getSettings(hotelId, screenType),
              updateSettings: (screenType, featuredServiceIds) =>
                mobileSettingsService.updateSettings({
                  hotelId,
                  screenType,
                  featuredServiceIds,
                }),
            },
          );
        } finally {
          // Also after a failure: the switch shows what the server holds now.
          await queryClient.invalidateQueries({
            queryKey: mobileSettingsKeys.all,
          });
        }
      });
    },
    [hotelId, queryClient],
  );

  return useMemo(
    () => ({
      featuredIds,
      isReady: settings !== undefined,
      loadFailed: settings === undefined && error != null,
      setFeatured,
    }),
    [featuredIds, settings, error, setFeatured],
  );
};
